import { Type } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Data, Route } from '@angular/router';
import { InputFillMode } from '@progress/kendo-angular-inputs';
import { SortDescriptor } from '@progress/kendo-data-query';
import { UnityEntitlement, UnityFeatureType, UnityPermissionType, UserMiniDto, WorkflowStateMiniDto } from '@unity/clients';
import { insideRoutes, outsideRoutes } from '@unity/routes';
import { icons } from '@unity/services/icon.service';

// Utility type that helps ensure a valid form schema
// Intentional use of `any` to prevent mismatches with nswag codegen types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormGroupControlSchema<T extends Record<string, any>> = {
  [K in keyof T]: FormControl<T[K]>;
};

export interface CanDeactivateGuard {
  canDeactivate: () => boolean;
}

export type UnityRouteData = Data & {
  feature?: UnityFeatureType;
};

export type InsideUnityRouteWithoutComponent = Omit<Route, 'component' | 'data' | 'children'> & {
  component?: undefined;
  data?: UnityRouteData;
  children?: InsideUnityRoute[];
  path: string;
};

export type InsideUnityRouteWithComponent = Omit<Route, 'component' | 'data' | 'children'> & {
  component: Type<unknown>;
  data: UnityRouteData & {
    authorize: UnityEntitlementPath | null;
  };
  children?: InsideUnityRoute[];
  path: string;
};

export type OutsideUnityRoute = Route;

export type InsideUnityRoute = InsideUnityRouteWithComponent | InsideUnityRouteWithoutComponent;

export type UnityRoute = InsideUnityRoute | OutsideUnityRoute;

// Recursively extracts all full route paths from a nested UnityRoutes structure,
// flattening them into a union of string paths. It prepends parent paths to child paths
// using template literal types.
type ExtractPaths<T, Parent extends string = ''> = T extends readonly (infer R)[]
  ? ExtractPaths<R, Parent>
  : T extends { path: infer P extends string; children?: infer C }
    ? `${Parent}${Parent extends '' ? '' : '/'}${P}` | ExtractPaths<C, `${Parent}${Parent extends '' ? '' : '/'}${P}`>
    : never;

// Combines the `path` values from insideRoutes and outsideRoutes into a union type
export type UnityRoutePaths = ExtractPaths<typeof insideRoutes> | ExtractPaths<typeof outsideRoutes>;

// Infers the type of a route segment:
// - If the segment is a parameter ending in "id" or "Id" (e.g., ":userId"), it's typed as `number`
// - If it's any other parameter (e.g., ":slug"), it's typed as `string`
// - Otherwise, it's a static segment and returned as-is (a string literal)
type RouteParameterType<T extends string> = T extends `:${string}${'id' | 'Id'}` ? number : T extends `:` ? string : T;

// Splits a route string into segments by '/' and maps each segment to its inferred parameter type
type RouteUrlSplit<S extends string> = S extends `${infer T}/${infer U}`
  ? [RouteParameterType<T>, ...RouteUrlTree<U>]
  : [RouteParameterType<S>];

// Converts the first segment of a route array to an absolute path (e.g., "agreements" -> "/agreements")
type RouteUrlAsAbsolute<S extends unknown[]> = S extends [infer X, ...infer Tail] ? (X extends string ? [`/${X}`, ...Tail] : never) : never;

// Handles the edge case for an empty route string by returning an empty array
type RouteUrlTree<S extends string> = S extends '' ? [] : RouteUrlSplit<S>;

// Generates an array of route segment types for each route path, with the first segment as absolute
export type UnityRouteUrlTrees = RouteUrlAsAbsolute<RouteUrlTree<UnityRoutePaths>> | ['.' | '/'];

// Soft API call typing, for clean generic service calls
export type ApiParams = Record<string, string> & {
  viewType?: 'detailed' | 'mini' | 'default';
};
export type ApiHeaders = Record<string, string>;
export interface ApiCall {
  path: string;
  params?: ApiParams;
  headers?: ApiHeaders;
  body?: unknown;
}

export type UnityFeatureTypeStrings = keyof typeof UnityFeatureType;
export type UnityPermissionTypeStrings = keyof typeof UnityPermissionType;
export type UnityEntitlementPath = `${UnityEntitlement}:${UnityPermissionTypeStrings}`;

// MFA query string parameters
export interface MfaParams {
  code: string;
  state: string;
}

export interface SharedUserGridDto {
  id: number;
  user_name: string;
  full_name: string;
  current_state: WorkflowStateMiniDto;
}

export interface AuditDto {
  created_dt: Date;
  last_modified_dt: Date;
  created_by_user: UserMiniDto;
  last_modified_by_user: UserMiniDto;
}

export interface UnityDto {
  id: number;
  current_state?: WorkflowStateMiniDto;
}

export interface DetailedDto extends AuditDto, UnityDto {}

export interface Paging<UnityDto> {
  count: number;
  data: UnityDto[];
}

export interface QuickActionUrlProps extends Omit<QuickActionProps, 'url' | 'callback'> {
  authorize: UnityEntitlementPath;
  url: UnityRouteUrlTrees;
}

export interface QuickActionCallbackProps extends Omit<QuickActionProps, 'url' | 'callback'> {
  authorize: UnityEntitlementPath;
  callback: () => void;
}

export interface QuickActionProps {
  authorize?: UnityEntitlementPath;
  authorizeOneOf?: UnityEntitlementPath[];
  url?: UnityRouteUrlTrees;
  callback?: () => void;
  query?: Record<string, string | number>;
  icon: keyof typeof icons;
  actionName: string;
  description: string;
  feature?: UnityFeatureType;
}

// For use in shared forms
export interface FormEditState {
  editing: boolean;
  fillMode: InputFillMode;
}

// For layout service
export interface SidebarLayoutConfig {
  horizontal: boolean;
  mainRow: number;
  mainCol: number;
  sideRow: number;
  sideCol: number;
}

// For sorting
export type CaseSensitivity = 'insensitive' | 'sensitive';
export interface SortConfig extends SortDescriptor {
  dir: 'asc' | 'desc';
  case_sensitive?: CaseSensitivity;
}

// SurveyJS
export enum SurveyJSProperties {
  UnityIsTemplateQuestion = 'unityIsTemplateQuestion',
  UnityIsGridQuestion = 'unityIsGridQuestion',
}

export interface GridifyQuery {
  filter?: string;
  page?: number;
  pageSize?: number;
  orderBy?: string;
}

export interface FormStep {
  label: string;
  isValid: boolean;
  page: number;
}
